/**
 * poolScope — the scope an Item Pool is born into.
 *
 * Canon Doc 03 §2.11A: an Item Pool is scoped to a Track / Cycle and that
 * scope is required. A pool with no cycle and no track has no criteria set
 * and nothing can rank it. This module turns the workspace's cycles and
 * tracks into the options a create dialog offers, and turns the user's pick
 * back into the `{ cycleId, trackId }` pair `createItemPool` persists.
 *
 * Pure: no stores, no React. Tested in poolScope.test.ts.
 */

export interface PoolScopeCycle {
  id: string;
  name: string;
  status: string;
}

export interface PoolScopeTrack {
  id: string;
  name: string;
  cycleId: string | null;
  status: string;
}

/**
 * A scope pick as the dialog holds it: a cycle, a standalone track, or
 * 'none' — an explicit "not yet". Doc 03 §2.11A: scope is required at lock,
 * so a draft may be assembled unscoped but cannot lock or rank until it has a
 * Track / Cycle. '' means the user has not chosen.
 */
export type PoolScopeKey = `cycle:${string}` | `track:${string}` | 'none' | '';

export const POOL_SCOPE_NONE: PoolScopeKey = 'none';

export interface PoolScopeOptions {
  cycles: PoolScopeCycle[];
  standaloneTracks: PoolScopeTrack[];
}

export interface ResolvedPoolScope {
  cycleId?: string;
  trackId?: string;
}

/** Cycles a new pool may still be created in. Closed cycles are excluded. */
const CLOSED_CYCLE_STATUSES = new Set(['completed', 'archived']);
/** Tracks a new pool may still be created in. */
const CLOSED_TRACK_STATUSES = new Set(['archived', 'final_rejected']);

const byName = <T extends { name: string }>(a: T, b: T) => a.name.localeCompare(b.name);

export function isOpenCycle(cycle: PoolScopeCycle): boolean {
  return !CLOSED_CYCLE_STATUSES.has(cycle.status);
}

export function isOpenTrack(track: PoolScopeTrack): boolean {
  return !CLOSED_TRACK_STATUSES.has(track.status);
}

/** The options a create dialog offers: open cycles, then open standalone tracks. */
export function buildPoolScopeOptions(
  cycles: readonly PoolScopeCycle[],
  tracks: readonly PoolScopeTrack[],
): PoolScopeOptions {
  return {
    cycles: cycles.filter(isOpenCycle).sort(byName),
    standaloneTracks: tracks.filter((t) => t.cycleId === null && isOpenTrack(t)).sort(byName),
  };
}

/** Open tracks inside one cycle, in name order. */
export function tracksForCycle(
  tracks: readonly PoolScopeTrack[],
  cycleId: string,
): PoolScopeTrack[] {
  return tracks.filter((t) => t.cycleId === cycleId && isOpenTrack(t)).sort(byName);
}

export function poolScopeKeyForCycle(cycleId: string): PoolScopeKey {
  return `cycle:${cycleId}`;
}

export function poolScopeKeyForTrack(trackId: string): PoolScopeKey {
  return `track:${trackId}`;
}

export function parsePoolScopeKey(
  key: string,
): { kind: 'cycle' | 'track' | 'none'; id: string } | null {
  if (key === 'none') return { kind: 'none', id: '' };
  if (key.startsWith('cycle:')) return { kind: 'cycle', id: key.slice('cycle:'.length) };
  if (key.startsWith('track:')) return { kind: 'track', id: key.slice('track:'.length) };
  return null;
}

/**
 * The scope key a dialog should open with. Honors the pinned cycle when it is
 * still open; otherwise the only cycle when there is exactly one; otherwise
 * the only standalone track when there is exactly one; otherwise nothing.
 */
export function defaultPoolScopeKey(
  options: PoolScopeOptions,
  pinnedCycleId?: string | null,
): PoolScopeKey {
  if (pinnedCycleId && options.cycles.some((c) => c.id === pinnedCycleId)) {
    return poolScopeKeyForCycle(pinnedCycleId);
  }
  if (options.cycles.length === 1 && options.standaloneTracks.length === 0) {
    return poolScopeKeyForCycle(options.cycles[0].id);
  }
  if (options.cycles.length === 0 && options.standaloneTracks.length === 1) {
    return poolScopeKeyForTrack(options.standaloneTracks[0].id);
  }
  return '';
}

/**
 * Turn the dialog's picks into what `createItemPool` persists.
 *
 * - cycle scope: `cycleId` always; `trackId` when the cycle has open tracks
 *   (the caller's pick, or the first when it made none).
 * - standalone track scope: `trackId` only.
 * - 'none': `{}` — an unscoped draft (Doc 03 §2.11A: scope is required at
 *   lock, not at birth). The store refuses to lock it until it is assigned.
 * - no choice (''): null — the dialog must refuse to submit.
 */
export function resolvePoolScope(
  key: string,
  tracks: readonly PoolScopeTrack[],
  pickedTrackId?: string,
): ResolvedPoolScope | null {
  const parsed = parsePoolScopeKey(key);
  if (!parsed) return null;
  if (parsed.kind === 'none') return {};
  if (parsed.kind === 'track') {
    const track = tracks.find((t) => t.id === parsed.id);
    if (!track) return null;
    return { trackId: track.id, ...(track.cycleId ? { cycleId: track.cycleId } : {}) };
  }
  const inCycle = tracksForCycle(tracks, parsed.id);
  if (inCycle.length === 0) return { cycleId: parsed.id };
  const picked = inCycle.find((t) => t.id === pickedTrackId) ?? inCycle[0];
  return { cycleId: parsed.id, trackId: picked.id };
}

/** Why the dialog will not submit without a choice. Shown verbatim to the user. */
export const POOL_SCOPE_REQUIRED_MESSAGE =
  'Choose the cycle or track this pool belongs to, or "Not yet" to assemble it first. An Item Pool is the comparison set for a ranking, so it needs a home before it can be locked or ranked.';

/** Does this pool have the Track / Cycle home the canon requires at lock? */
export function isPoolScoped(pool: { cycleId?: string | null; trackId?: string | null }): boolean {
  return Boolean(pool.cycleId || pool.trackId);
}

/** Why an unscoped pool will not lock. Shown verbatim to the user. */
export const POOL_LOCK_REQUIRES_SCOPE_MESSAGE =
  'This pool has no cycle or track yet. Assign one before locking — the criteria set and the ranking sessions live on the track (Doc 03 §2.5, §2.11A).';

/**
 * One pool per track. The criteria set and the ranking sessions hang off the
 * track's pool, so a second live pool on the same track splits the ranking.
 * Returns the pool already living on `trackId` (ignoring `excludePoolId` and
 * retired pools), or null. The client-side read of the rule; the server holds
 * it too (migration 761: create_item_pool refuses with blocked_by, and the
 * trg_item_pools_one_per_track trigger refuses any other write path).
 */
const RETIRED_POOL_STATUSES = new Set(['archived', 'superseded']);

export function findPoolOnTrack<T extends { id: string; name: string; trackId?: string | null; status?: string }>(
  pools: readonly T[],
  trackId: string | null | undefined,
  excludePoolId?: string,
): T | null {
  if (!trackId) return null;
  return (
    pools.find(
      (p) => p.trackId === trackId && p.id !== excludePoolId && !RETIRED_POOL_STATUSES.has(p.status ?? ''),
    ) ?? null
  );
}

/** Shown verbatim when the chosen track already has a pool. */
export function trackAlreadyHasPoolMessage(poolName: string): string {
  return `This track already has a pool: ${poolName}`;
}

/** Where a pool opens. */
export function poolPath(poolId: string): string {
  return `/prioritization/lists/${poolId}`;
}

/** Label for the explicit "not yet" choice. */
export const POOL_SCOPE_NONE_LABEL = "Not yet — I'll assign a cycle later";

/** What to show when the workspace has nowhere to put a pool yet. */
export const POOL_SCOPE_NONE_AVAILABLE_MESSAGE =
  'No open cycle or standalone track yet. You can assemble the pool now and assign it when the cycle exists.';
